import {
  INTERESTED_LIMIT_ERROR_CODE,
  SQUAD_LIMIT_ERROR_CODE,
  minTierForUnlimitedInterestedMarkers,
  minTierForUnlimitedSquads,
  memoriesPhotoUnlockTier,
  MEMORY_PHOTO_LIMIT_ERROR_CODE,
  type ConsumerTier,
} from "@pulse/shared";

export class InterestedLimitError extends Error {
  readonly code = INTERESTED_LIMIT_ERROR_CODE;
  readonly max: number;
  readonly unlockTier: ConsumerTier;

  constructor(max: number) {
    super(INTERESTED_LIMIT_ERROR_CODE);
    this.name = "InterestedLimitError";
    this.max = max;
    this.unlockTier = minTierForUnlimitedInterestedMarkers();
  }
}

export function isInterestedLimitError(error: unknown): error is InterestedLimitError {
  return error instanceof InterestedLimitError;
}

export function parseInterestedLimitFromPostgrest(error: unknown): InterestedLimitError | null {
  if (!error || typeof error !== "object") return null;
  const message = "message" in error ? String((error as { message: string }).message) : "";
  if (!message.includes(INTERESTED_LIMIT_ERROR_CODE)) return null;

  const detail =
    "details" in error
      ? String((error as { details: string }).details)
      : "hint" in error
        ? String((error as { hint: string }).hint)
        : "";

  const maxMatch = detail.match(/"max"\s*:\s*(\d+)/) ?? message.match(/max[":\s]+(\d+)/i);
  const max = maxMatch ? Number(maxMatch[1]) : 50;
  return new InterestedLimitError(max);
}

export const SUBSCRIPTION_REQUIRED_ERROR_CODE = "SUBSCRIPTION_REQUIRED";

export class SubscriptionRequiredError extends Error {
  readonly code = SUBSCRIPTION_REQUIRED_ERROR_CODE;

  constructor() {
    super(SUBSCRIPTION_REQUIRED_ERROR_CODE);
    this.name = "SubscriptionRequiredError";
  }
}

export function isSubscriptionRequiredError(error: unknown): error is SubscriptionRequiredError {
  return error instanceof SubscriptionRequiredError;
}

export function postgrestErrorMessage(error: unknown): string {
  if (!error || typeof error !== "object") return "";
  return "message" in error ? String((error as { message: string }).message) : "";
}

export function parseSubscriptionRequiredFromPostgrest(error: unknown): SubscriptionRequiredError | null {
  const message = postgrestErrorMessage(error);
  if (!message.includes(SUBSCRIPTION_REQUIRED_ERROR_CODE)) return null;
  return new SubscriptionRequiredError();
}

export function mapInterestedInsertError(error: unknown): never {
  const limit = parseInterestedLimitFromPostgrest(error);
  if (limit) throw limit;
  const subscription = parseSubscriptionRequiredFromPostgrest(error);
  if (subscription) throw subscription;
  throw error;
}

type InterestedToggleErrorLabels = {
  interestedLimit: string;
  subscriptionProvisioning: string;
  interestedToggleFailed: string;
};

export function formatInterestedToggleError(
  error: unknown,
  labels: InterestedToggleErrorLabels,
  limitMeta?: { max: number; tier: string },
): string {
  if (isInterestedLimitError(error)) {
    return labels.interestedLimit
      .replace("{{max}}", String(error.max))
      .replace("{{tier}}", limitMeta?.tier ?? "");
  }
  if (isSubscriptionRequiredError(error)) {
    return labels.subscriptionProvisioning;
  }
  const message = error instanceof Error ? error.message : postgrestErrorMessage(error);
  if (message.includes(INTERESTED_LIMIT_ERROR_CODE)) {
    return labels.interestedLimit
      .replace("{{max}}", String(limitMeta?.max ?? 50))
      .replace("{{tier}}", limitMeta?.tier ?? "");
  }
  if (message.includes(SUBSCRIPTION_REQUIRED_ERROR_CODE)) {
    return labels.subscriptionProvisioning;
  }
  if (message.trim()) return message;
  return labels.interestedToggleFailed;
}

export class SquadLimitError extends Error {
  readonly code = SQUAD_LIMIT_ERROR_CODE;
  readonly max: number;
  readonly unlockTier: ConsumerTier;

  constructor(max: number) {
    super(SQUAD_LIMIT_ERROR_CODE);
    this.name = "SquadLimitError";
    this.max = max;
    this.unlockTier = minTierForUnlimitedSquads();
  }
}

export function isSquadLimitError(error: unknown): error is SquadLimitError {
  return error instanceof SquadLimitError;
}

export function parseSquadLimitFromPostgrest(error: unknown): SquadLimitError | null {
  if (!error || typeof error !== "object") return null;
  const message = "message" in error ? String((error as { message: string }).message) : "";
  if (!message.includes(SQUAD_LIMIT_ERROR_CODE)) return null;

  const detail =
    "details" in error
      ? String((error as { details: string }).details)
      : "hint" in error
        ? String((error as { hint: string }).hint)
        : "";

  const maxMatch = detail.match(/"max"\s*:\s*(\d+)/) ?? message.match(/max[":\s]+(\d+)/i);
  const max = maxMatch ? Number(maxMatch[1]) : 1;
  return new SquadLimitError(max);
}

export class MemoriesPhotoLimitError extends Error {
  readonly code = MEMORY_PHOTO_LIMIT_ERROR_CODE;
  readonly max: number;
  readonly unlockTier: ConsumerTier;

  constructor(max: number) {
    super(MEMORY_PHOTO_LIMIT_ERROR_CODE);
    this.name = "MemoriesPhotoLimitError";
    this.max = max;
    this.unlockTier = memoriesPhotoUnlockTier(max);
  }
}

export function isMemoriesPhotoLimitError(error: unknown): error is MemoriesPhotoLimitError {
  return error instanceof MemoriesPhotoLimitError;
}

export function parseMemoriesPhotoLimitFromPostgrest(error: unknown): MemoriesPhotoLimitError | null {
  if (!error || typeof error !== "object") return null;
  const message = "message" in error ? String((error as { message: string }).message) : "";
  if (!message.includes(MEMORY_PHOTO_LIMIT_ERROR_CODE)) return null;

  const detail =
    "details" in error
      ? String((error as { details: string }).details)
      : "hint" in error
        ? String((error as { hint: string }).hint)
        : "";

  const maxMatch = detail.match(/"max"\s*:\s*(\d+)/) ?? message.match(/max[":\s]+(\d+)/i);
  const max = maxMatch ? Number(maxMatch[1]) : 5;
  return new MemoriesPhotoLimitError(max);
}
